import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { Task, TaskCreate, TaskUpdate, TaskStatus, TaskPriority } from '@/app/types/task';
import { useQuery } from '@tanstack/react-query';
import { listParties } from '@/app/services/partyService';
import { listUsers } from '@/app/services/userManagementService';
import { useRole } from '@/app/hooks/useRole';

// Zod Schema for Task Form
const formSchema = z.object({
  title: z.string().min(1, 'Task title is required').max(200),
  description: z.string().optional().nullable(),
  status: z.nativeEnum(TaskStatus).optional(),
  priority: z.nativeEnum(TaskPriority).optional(),
  due_date: z.string().optional().nullable(),
  assigned_to_user_id: z.number().optional().nullable(),
  parent_entity_id: z.number().optional().nullable(),
  task_category: z.string().optional().nullable(),
});

type TaskFormValues = z.infer<typeof formSchema>;

interface TaskFormProps {
  onSubmit: (data: TaskCreate | TaskUpdate) => void;
  defaultValues?: Partial<Task>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  prefilledPartyId?: number;
}

/**
 * TaskForm Component
 *
 * Shared form component for creating and editing tasks
 * Uses React Hook Form with Zod validation
 *
 * @param onSubmit - Callback function when form is submitted
 * @param defaultValues - Default values for edit mode
 * @param isLoading - Loading state for submit button
 * @param mode - 'create' or 'edit' mode
 * @param prefilledPartyId - Pre-filled party ID for quick task creation
 */
export const TaskForm: React.FC<TaskFormProps> = ({
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  prefilledPartyId,
}) => {
  const { isClientManager } = useRole();

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: defaultValues?.title || '',
      description: defaultValues?.description || '',
      status: defaultValues?.status || TaskStatus.OPEN,
      priority: defaultValues?.priority || TaskPriority.MEDIUM,
      due_date: defaultValues?.due_date || '',
      assigned_to_user_id: defaultValues?.assigned_to_user_id || null,
      parent_entity_id: defaultValues?.parent_entity_id || prefilledPartyId || null,
      task_category: defaultValues?.task_category || '',
    },
  });

  // Update parent_entity_id when prefilledPartyId changes
  useEffect(() => {
    if (prefilledPartyId && mode === 'create') {
      form.setValue('parent_entity_id', prefilledPartyId);
    }
  }, [prefilledPartyId, mode, form]);

  // Fetch parties for dropdown (reduced limit)
  const { data: partiesData } = useQuery({
    queryKey: ['parties', { limit: 100 }],
    queryFn: () => listParties({ limit: 100, skip: 0 }),
  });

  // Fetch users for assignment dropdown (only for non-client managers)
  const { data: usersData } = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: () => listUsers({ limit: 100, skip: 0 }),
    enabled: !isClientManager, // Don't fetch if client_manager
  });

  const handleSubmit: SubmitHandler<TaskFormValues> = (values) => {
    // If a party is selected, set entity type and id; otherwise send null
    const hasParty = values.parent_entity_id != null;

    // Clean up data before submitting
    const dataToSend: any = {
      title: values.title,
      description: values.description || null,
      status: values.status,
      priority: values.priority,
      due_date: values.due_date || null,
      assigned_to_user_id: values.assigned_to_user_id || null,
      parent_entity_type: hasParty ? 'party' : null,
      parent_entity_id: hasParty ? values.parent_entity_id : null,
      task_category: values.task_category || null,
    };

    // In create mode, include all fields
    // In edit mode, only include changed fields
    onSubmit(mode === 'edit' ? dataToSend : dataToSend);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        {/* Title */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title*</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g., Follow up with customer" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  value={field.value || ''}
                  placeholder="Add task details, notes, or instructions..."
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Priority and Status Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Priority */}
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Priority</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={TaskPriority.LOW}>Low</SelectItem>
                    <SelectItem value={TaskPriority.MEDIUM}>Medium</SelectItem>
                    <SelectItem value={TaskPriority.HIGH}>High</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Status */}
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={TaskStatus.OPEN}>Open</SelectItem>
                    <SelectItem value={TaskStatus.COMPLETED}>Completed</SelectItem>
                    <SelectItem value={TaskStatus.CANCELLED}>Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Due Date and Assigned To Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Due Date */}
          <FormField
            control={form.control}
            name="due_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Due Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Assigned To */}
          <FormField
            control={form.control}
            name="assigned_to_user_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assigned To</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value === 'unassigned' ? null : parseInt(value))}
                  value={field.value?.toString() || 'unassigned'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {usersData?.items?.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.full_name || user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Party (Optional) */}
        <FormField
          control={form.control}
          name="parent_entity_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Party (Optional)</FormLabel>
              <Select
                onValueChange={(value) => field.onChange(value === 'none' ? null : parseInt(value))}
                value={field.value?.toString() || 'none'}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select party" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {partiesData?.items?.map((party) => (
                    <SelectItem key={party.id} value={party.id.toString()}>
                      {party.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>Link this task to a specific party</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Category */}
        <FormField
          control={form.control}
          name="task_category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <FormControl>
                <Input {...field} value={field.value || ''} placeholder="e.g., Follow-up, Support, Sales" />
              </FormControl>
              <FormDescription>Optional: categorize your task for better organization</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Submit Button */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Create Task' : 'Update Task'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
